import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Observable} from 'rxjs';
import {Action, select, Store} from '@ngrx/store';
import * as actionsUi from './displaysteuerung.actions.ui';
import {filter, flatMap, map, withLatestFrom} from 'rxjs/operators';
import {PageState} from './displaysteuerung.reducer';
import {GetBereichDetailAdminAction, GetBereicheListeAdminAction, PostBereichEntfernenAction, PostBereichKopfdatenAktualisierenAction,
    PostBereichRolleEntfernenAction, PostBereichRolleHinzufuegenMultiAction, PostBereichTriggerAktualisierenAction, PostBereichTriggerAusloesenAction,
    PostBereichTriggerEntfernenAction, PostBereichTriggerHinzufuegenAction} from '../../../client-ngrx/displaysteuerung';
import {AktuellenBereichGesetztAction} from './displaysteuerung.actions.ui';
import {getBereichDetail, getSelectedBereich, getSelectedRolle, getSelectedTrigger} from './displaysteuerung.selectors';
import {BereichKopfdatenRequest, BereichRequest, BereichRolleMultiRequest, BereichRolleRequest, BereichTriggerDatenRequest,
    BereichTriggerRequest} from '../../../clients/displaysteuerung';
import {UuidService} from '@orga-app/providers/uuid.service';

@Injectable()
export class DisplaysteuerungEffectsUi {

    pageReset$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(actionsUi.PAGE_STATE_RESET)
        , map(() => {
            return new GetBereicheListeAdminAction();
        })
    ));

    bereicheListeLaden$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(actionsUi.BEREICHE_LISTE_LADEN)
        , map(() => {
            return new GetBereicheListeAdminAction();
        })
    ));

    bereichAusgewaehlt$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(actionsUi.AKTUELLEN_BEREICH_GESETZT)
        , map((x: AktuellenBereichGesetztAction) => {
            return new GetBereichDetailAdminAction(x.payload.bereich_id);
        })
    ));

    bereichEntfernen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.AktuellenBereichGesetztAction>(actionsUi.BEREICH_ENTFERNEN)
        , withLatestFrom(this.store$.pipe(select(getSelectedBereich)))
        , filter(([, bereich]) => bereich !== null)
        , map(([, bereich]) => {
            return new PostBereichEntfernenAction(<BereichRequest> {
                bereich_id: bereich.bereich_id,
                zeitpunkt_utc: new Date()
            });
        })
    ));

    kopfdatenSpeichern$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.BereichKopfdatenSpeichernAction>(actionsUi.BEREICH_KOPFDATEN_SPEICHERN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)))
        , filter(([, detail]) => detail !== null)
        , map(([, detail]) => {
            let bereichId = detail.kopfdaten.bereich_id;
            if (bereichId === '') {
                bereichId = this.uuid.generateUUID();
            }
            const request: BereichKopfdatenRequest = {
                bereich_id: bereichId,
                beschreibung: detail.kopfdaten.beschreibung,
                externe_url: detail.kopfdaten.externe_url,
                ist_externe_url: detail.kopfdaten.ist_externe_url,
                ist_trigger_liste: detail.kopfdaten.ist_trigger_liste,
                reihenfolge: detail.kopfdaten.reihenfolge,
                titel: detail.kopfdaten.titel,
                zusatz_beschreibung: detail.kopfdaten.zusatz_beschreibung,
            };
            return new PostBereichKopfdatenAktualisierenAction(request);
        })
    ));

    rollenSetzen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.BereichBerechtigungRollenSetzenAction>(actionsUi.BEREICH_BERECHTIGUNG_ROLLEN_SETZEN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)))
        , filter(([, detail]) => detail !== null)
        , map(([x, detail]) => {
            const request: BereichRolleMultiRequest = {
                bereich_id: detail.kopfdaten.bereich_id,
                rolle_ids: x.payload,
            };
            return new PostBereichRolleHinzufuegenMultiAction(request);
        })
    ));

    rolleEntfernen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.BereichBerechtigungRolleEntfernenAction>(actionsUi.BEREICH_BERECHTIGUNG_ROLLE_ENTFERNEN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)),
            this.store$.pipe(select(getSelectedRolle)))
        , filter(([, detail, rolle]) => detail !== null && rolle !== null)
        , map(([, detail, rolle]) => {
            const request: BereichRolleRequest = {
                bereich_id: detail.kopfdaten.bereich_id,
                rolle_id: rolle.rolle_id,
            };
            return new PostBereichRolleEntfernenAction(request);
        })
    ));

    aktuellenTriggerIcon$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.AktuellenTriggerIconGesetztAction>(actionsUi.AKTUELLEN_TRIGGER_ICON_GESETZT)
        , withLatestFrom(this.store$.pipe(select(getSelectedTrigger)),
            this.store$.pipe(select(getBereichDetail)))
        , filter(([, trigger, detail]) => trigger !== null && detail !== null)
        , map(([x, trigger, detail]) => {
            const request: BereichTriggerDatenRequest = {
                trigger_id: trigger.trigger_id,
                bereich_id: detail.kopfdaten.bereich_id,
                icon: x.payload,
                reihenfolge: trigger.reihenfolge,
                anzeige_name: trigger.anzeige_name,
            };
            return new PostBereichTriggerAktualisierenAction(request);
        })
    ));

    aktuellenBereichTriggerHinzufuegen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.BereichTriggerHinzufuegenAction>(actionsUi.BEREICH_TRIGGER_HINZUFUEGEN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)))
        , filter(([, detail]) => detail !== null)
        , flatMap(([x, detail]) => {
            const actions: Array<Action> = [];
            let i = 0;
            x.payload.forEach(trigger => {
                actions.push(new PostBereichTriggerHinzufuegenAction(<BereichTriggerDatenRequest>{
                    trigger_id: trigger.id,
                    bereich_id: detail.kopfdaten.bereich_id,
                    icon: 'play',
                    anzeige_name: trigger.name,
                    reihenfolge: detail.trigger.length + i,
                    zeitpunkt_utc: new Date()
                }));
                i = 1 + i;
            });
            return actions;
        })
    ));

    aktuellenBereichTriggerEntfernen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.BereichTriggerEntfernenAction>(actionsUi.BEREICH_TRIGGER_ENTFERNEN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)),
            this.store$.pipe(select(getSelectedTrigger)))
        , filter(([, detail, trigger]) => detail !== null && trigger !== null)
        , map(([, detail, trigger]) => {
            return new PostBereichTriggerEntfernenAction(<BereichTriggerRequest>{
                trigger_id: trigger.trigger_id,
                bereich_id: detail.kopfdaten.bereich_id,
                zeitpunkt_utc: new Date()
            });
        })
    ));

    aktuellenBereichTriggerAusloesen$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<actionsUi.AktuellenTriggerAusloesenAction>(actionsUi.AKTUELLEN_TRIGGER_AUSLOESEN)
        , withLatestFrom(this.store$.pipe(select(getBereichDetail)),
            this.store$.pipe(select(getSelectedTrigger)))
        , filter(([, detail, trigger]) => detail !== null && trigger !== null)
        , map(([, detail, trigger]) => {
            return new PostBereichTriggerAusloesenAction(<BereichTriggerRequest>{
                trigger_id: trigger.trigger_id,
                bereich_id: detail.kopfdaten.bereich_id,
                zeitpunkt_utc: new Date()
            });
        })
    ));

    constructor(private actions$: Actions,
                private uuid: UuidService,
                private store$: Store<PageState>) {
    }
}
